import React from "react";
import { useTranslation } from "react-i18next";

import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { Box, Button, Divider, Grid2 as Grid, IconButton, InputAdornment, TextField, Typography } from "@mui/material";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import { Select } from "components/forms";
import dayjs from "dayjs";
import { FormikProps } from "formik";
import { useDateFormatSelect } from "store";
import { FacilityFee, FrequencyType, PercentageOf } from "store/types/deal/debt";
import toIsoMidday from "utils/to-iso-midday";

import { FormValues } from "../schemas";
import { getFrequencyOptions } from "./constants";

interface Props {
  formik: FormikProps<FormValues>;
}

export const FacilityWizardFeesSection: React.FC<Props> = ({ formik }) => {
  const { t } = useTranslation("debt");
  const dateFormat = useDateFormatSelect();

  const getFeeError = (index: number, field: keyof FacilityFee) => {
    const feeErrors = formik.errors.fees?.[index];
    if (feeErrors && typeof feeErrors !== "string") {
      return feeErrors[field];
    }
    return undefined;
  };

  const isFeeTouched = (index: number, field: keyof FacilityFee) => {
    const feeTouched = formik.touched.fees?.[index];
    if (feeTouched && typeof feeTouched !== "boolean") {
      return feeTouched[field];
    }
    return false;
  };

  const handleAddFee = () => {
    const newFee: FacilityFee & { key: string } = {
      id: 0,
      key: Date.now().toString(),
      type: "custom_one_time",
      start_date: null,
      end_date: null,
      frequency: "month",
      amount: 0,
      percentage_of: null
    };
    formik.setFieldValue("fees", [...formik.values.fees, newFee]);
  };

  const handleRemoveFee = (index: number) => {
    const updatedFees = formik.values.fees.filter((_, i) => i !== index);
    formik.setFieldValue("fees", updatedFees);
  };

  const handleFeeChange = (index: number, field: keyof FacilityFee, value: FacilityFee[keyof FacilityFee]) => {
    const updatedFees = [...formik.values.fees];
    updatedFees[index] = { ...updatedFees[index], [field]: value };
    formik.setFieldValue("fees", updatedFees);
  };

  return (
    <Box>
      <Box sx={{ mb: 5 }}>
        <Typography variant="subtitle1" sx={{ mb: 1 }}>
          Fees
        </Typography>
        <Divider />
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        {formik.values.fees.map((fee, index) => (
          <Box
            key={fee.key || fee.id}
            sx={{ p: 2, border: "1px solid", borderColor: "divider", borderRadius: 2 }}
          >
            {/* Row 1: fee type + delete */}
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
              <Select
                name={`fees.${index}.type`}
                label=""
                variant="standard"
                value={fee.type}
                items={[
                  { key: "credit_facility", value: "credit_facility", text: "Credit facility fee" },
                  { key: "arrangement", value: "arrangement", text: "Arrangement fee" },
                  { key: "custom_one_time", value: "custom_one_time", text: "One-time fee" }
                ]}
                onChange={(e) => handleFeeChange(index, "type", e.target.value as FacilityFee["type"])}
                sx={{
                  "&:before": { display: "none" },
                  "&:after": { display: "none" },
                  "& .MuiSelect-select": { paddingTop: 0, paddingBottom: 0, fontWeight: 600 }
                }}
              />
              <IconButton onClick={() => handleRemoveFee(index)} color="error" size="sm">
                <DeleteIcon fontSize="sm" />
              </IconButton>
            </Box>

            {/* Row 2: fields */}
            <Grid container spacing={2} alignItems="center">
              {fee.type === "credit_facility" ? (
                <>
                  <Grid size={4}>
                    <DesktopDatePicker
                      format={dateFormat}
                      views={["year", "month", "day"]}
                      label="Start Date"
                      value={fee.start_date ? dayjs(fee.start_date) : null}
                      onChange={(newValue) =>
                        handleFeeChange(index, "start_date", newValue ? toIsoMidday(newValue) : null)
                      }
                      sx={{ width: "100%" }}
                      slotProps={{
                        textField: {
                          error: isFeeTouched(index, "start_date") && Boolean(getFeeError(index, "start_date")),
                          helperText: isFeeTouched(index, "start_date") ? getFeeError(index, "start_date") : undefined
                        }
                      }}
                    />
                  </Grid>
                  <Grid size={4}>
                    <DesktopDatePicker
                      format={dateFormat}
                      views={["year", "month", "day"]}
                      label="End Date"
                      value={fee.end_date ? dayjs(fee.end_date) : null}
                      onChange={(newValue) =>
                        handleFeeChange(index, "end_date", newValue ? toIsoMidday(newValue) : null)
                      }
                      sx={{ width: "100%" }}
                      slotProps={{
                        textField: {
                          error: isFeeTouched(index, "end_date") && Boolean(getFeeError(index, "end_date")),
                          helperText: isFeeTouched(index, "end_date") ? getFeeError(index, "end_date") : undefined
                        }
                      }}
                    />
                  </Grid>
                  <Grid size={4}>
                    <Select
                      name={`fees.${index}.frequency`}
                      label="Frequency"
                      variant="outlined"
                      fullWidth
                      value={fee.frequency}
                      items={getFrequencyOptions(t)}
                      error={isFeeTouched(index, "frequency") && Boolean(getFeeError(index, "frequency"))}
                      helperText={isFeeTouched(index, "frequency") ? getFeeError(index, "frequency") : undefined}
                      onChange={(e) => handleFeeChange(index, "frequency", e.target.value as FrequencyType)}
                    />
                  </Grid>
                </>
              ) : (
                <Grid size={6}>
                  <DesktopDatePicker
                    format={dateFormat}
                    views={["year", "month", "day"]}
                    label="Fee Date"
                    value={fee.start_date ? dayjs(fee.start_date) : null}
                    onChange={(newValue) =>
                      handleFeeChange(index, "start_date", newValue ? toIsoMidday(newValue) : null)
                    }
                    sx={{ width: "100%" }}
                    slotProps={{
                      textField: {
                        error: isFeeTouched(index, "start_date") && Boolean(getFeeError(index, "start_date")),
                        helperText: isFeeTouched(index, "start_date") ? getFeeError(index, "start_date") : undefined
                      }
                    }}
                  />
                </Grid>
              )}

              <Grid size={fee.type === "credit_facility" ? 6 : 3}>
                <Select
                  name={`fees.${index}.percentage_of`}
                  label="Type"
                  variant="outlined"
                  fullWidth
                  value={fee.percentage_of ?? "fixed"}
                  items={[
                    { key: "fixed", value: "fixed", text: "Fixed amount" },
                    { key: "total_commitment", value: "total_commitment", text: "% of commitment" },
                    { key: "unfunded_commitment", value: "unfunded_commitment", text: "% of unfunded commitment" },
                    { key: "funded_amount", value: "funded_amount", text: "% of funded amount" }
                  ]}
                  onChange={(e) =>
                    handleFeeChange(
                      index,
                      "percentage_of",
                      e.target.value === "fixed" ? null : (e.target.value as PercentageOf)
                    )
                  }
                />
              </Grid>

              <Grid size={fee.type === "credit_facility" ? 6 : 3}>
                <TextField
                  fullWidth
                  type="number"
                  label={fee.percentage_of ? "Fee Rate" : "Amount"}
                  value={fee.amount}
                  onChange={(e) => handleFeeChange(index, "amount", parseFloat(e.target.value) || 0)}
                  error={isFeeTouched(index, "amount") && Boolean(getFeeError(index, "amount"))}
                  helperText={isFeeTouched(index, "amount") ? getFeeError(index, "amount") : undefined}
                  slotProps={{
                    input: {
                      endAdornment: fee.percentage_of ? <InputAdornment position="end">%</InputAdornment> : undefined
                    }
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        ))}
      </Box>

      <Button variant="outlined" startIcon={<AddIcon />} onClick={handleAddFee} sx={{ mt: 2 }}>
        Add Fee
      </Button>
    </Box>
  );
};